import React, { useEffect, useState, useRef } from "react";
import { Button, Space, Form, Input, Menu } from 'antd';
import { SearchOutlined, PlusCircleOutlined, EditOutlined, MergeOutlined } from '@ant-design/icons';
import useHTTP from "@/hooks/useHTTP.mjs";
import runAsync from "@/script/runAsync.mjs";
import {Modal, useOpenModal } from '@/components/Modal/index.mjs';
import BranchDlg from './BranchDlg.jsx';
import Permission from '@/components/Permission.jsx';
import openWindow from '@/script/openWindow.mjs';
import { getBaseline } from "@/script/SelectDb.mjs";

export const MergeDlg = ({prjId, srcSVer, tarSVer, onCall, onOk, ...props}) => {
    const ref = useRef({});
    const http = useHTTP();
    const [form] = Form.useForm();
    const openModal = useOpenModal();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
            init();
    }, []);

    const init = () => {

        form.resetFields();

        if (srcSVer) {
            queryBaseline('src', srcSVer);
        }

        if (tarSVer) {
            ref.current.baselineSVer = tarSVer;

            queryBaseline('tar', tarSVer);
        } else {
            ref.current.baselineSVer = 1;

            queryBaseline('tar', 1);
        }
    }

    // 查询目标分支
    const queryBaseline = async (type, sVer) => {
        try {
            setLoading(true);
            const result = await runAsync(
                async () => {
                    return await http.get(`/onePrjBaseline`, { prjId: prjId, sVer: sVer });
                }, {
                error: { content: (err) => `查询失败: ${err.message}` },
                loading: { show: false },
                success: { show: false },
            });

            if (type == 'src' && result) {
                const { pkid, pblName, sVer } = result;
                ref.current.srcBaselineId = pkid; // 分支号ID
                ref.current.srcBaselineSVer = sVer;

                const srcName = pblName + '/' + sVer;
                form.setFieldsValue({ srcName });
            }

            if (type == 'tar' && result) {
                const { pkid, pblName, sVer } = result;
                ref.current.baselineId = pkid; // 分支号ID
                ref.current.baselineSVer = sVer;

                const tarName = pblName + '/' + sVer;
                form.setFieldsValue({ tarName });
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    // 查询: 合并
    const queryMerge = async (prjId, srcSVer, tarSVer) => {
        let merge = null;
        try {
            const data = await runAsync(
                async () => {
                    return await http.get(`/mergeInc/listMerge`, {
                        prjId: prjId,
                        srcSVer,
                        tarSVer,
                    })
                },
                {
                    error: { content: (err) => `查询失败: ${err.message}` },
                    loading: { show: false },
                    success: { show: false },
                }
            );
            if (data && data.length > 0) {
                merge = data[0];
            }
        } catch (err) {
            console.error(err);
        } finally {
            return merge;
        }
    }

    // 创建分支
    const createBranch = async () => {
        try {
            setLoading(true);
            const formField = await form.validateFields();
            formField.prjId = prjId;
            // 源模型成品ID
            formField.srcBaselineId = ref.current.srcBaselineId;
            formField.srcSVer = ref.current.srcBaselineSVer;
            // 目标分支ID
            formField.tarBaselineId = ref.current.baselineId;
            formField.tarSVer = ref.current.baselineSVer;

            // 校验源分支不能等于目标分支
            if (formField.srcBaselineId === formField.tarBaselineId) {
                Modal.error({
                    title: '源分支不能等于目标分支',
                    content: '请选择正确的源分支和目标分支',
                });
                return;
            }
            let merge = await queryMerge(prjId, formField.srcSVer, formField.tarSVer);
            if (!merge) {
                const newMerge = await runAsync(
                    async () => {
                        return await http.post(`/mergeInc/createMerge`, formField, {
                            headers: {
                                prjId,
                                sVer: ref.current.baselineSVer
                            }
                        });
                    }, {
                    error: { content: (err) => `创建分支合并失败: ${err.message}` },
                    loading: { show: true },
                    success: { show: true },
                });

                if (newMerge) {
                    const { pkid, prjId, tarSVer } = newMerge;
                    openWindow('/MergeIncList', { pkid, prjId, tarSVer });
                }

                onOk()
                if (onCall) onCall();
            } else {
                // 若存在，项目分支记录 的状态为 “未开始合并”或“已部分合并”，则跳转（同 2 ，打开新页签）
                if (merge.stsCode == 'NOT_START' || merge.stsCode == 'PART_MERGED' || merge.stsCode == 'FAIL_MERGED') {
                    const { pkid, prjId, tarSVer } = merge;
                    openWindow('/MergeIncList', { pkid, prjId, tarSVer });

                    onOk()
                    if (onCall) onCall();
                }
                // 若为“合并处理中” ，提示相应错误信息“当前分支合并正在处理中，请稍后再处理合并！”，点击确定，进入分支合并详情页面（打开新页签）
                if (merge.stsCode == 'PROCESSING') {
                    Modal.confirm({
                        content: '当前分支合并正在处理中，请稍后再处理合并！',
                        cancelText: '取消',
                        onOk: async () => {
                            const { pkid } = merge;
                            openWindow('/MergeIncDetail', { pkid });

                            onOk()
                            if (onCall) onCall();
                        }
                    });
                }
                // 若为“已完成合并” ，提示相应错误信息“当前分支合并已完成！”，点击确定，进入分支合并详情页面（打开新页签）
                if (merge.stsCode == 'FINISH_MERGED') {
                    Modal.confirm({
                        content: '当前分支合并已完成！',
                        cancelText: '取消',
                        onOk: async () => {
                            const { pkid } = merge;
                            openWindow('/MergeIncDetail', { pkid });

                            onOk()
                            if (onCall) onCall();
                        }
                    });
                }
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    // 选择：项目分支（源）
    const openSrcBranch = async () => {
        const result = await openModal(<BranchDlg prjId={prjId} />); // pblType={'INCREMENT'}

        if (result) {
            const { pkid, pblName, sVer } = result;
            ref.current.srcBaselineId = pkid; // 分支号ID
            ref.current.srcBaselineSVer = sVer;

            const srcName = pblName + '/' + sVer;
            form.setFieldsValue({ srcName });
        }
    }

    // 选择：项目分支（目标）
    const openBranch = async () => {
        const result = await openModal(<BranchDlg prjId={prjId} />);

        if (result) {
            const { pkid, pblName, sVer } = result;
            ref.current.baselineId = pkid; // 分支号ID
            ref.current.baselineSVer = sVer;

            const tarName = pblName + '/' + sVer;
            form.setFieldsValue({ tarName });
        }
    }

    return (
        <Modal
            title={"合并分支"}
            width={600}
            style={{ top: 20 }}
            bodyStyle={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#fff', padding: 12 }}
            footer={[
                <Space>
                    <Button onClick={() => onOk()}>取消</Button>

                    {/*<Permission permission="MERGE_BRANCH" projects={[prjId + ref.current.baselineSVer]}>*/}
                    <Button type="primary" onClick={createBranch} loading={loading}>确定</Button>
                    {/*</Permission>*/}
                </Space>
            ]}
            {...props}
        >
            <Form form={form} layout="vertical">
                <Form.Item name="srcName" label="源分支" rules={[{ required: true, message: '请选择源分支' }]}>
                    <Input placeholder="请选择源分支" addonAfter={<SearchOutlined onClick={() => openSrcBranch()} />} readOnly />
                </Form.Item>

                {/*style={tarSVer ? { display: 'none' } : {}}*/}
                <Form.Item name="tarName" label="目标分支" rules={[{ required: true, message: '请选择目标分支' }]}>
                    <Input placeholder="请选择目标分支" addonAfter={<SearchOutlined onClick={() => openBranch()} />} readOnly />
                </Form.Item>
            </Form>
        </Modal>
    );
}

const Pane = ({ prjId, srcSVer, tarSVer, onCall }) => {
    const openModal = useOpenModal();
    const [tarType, setTarType] = useState();

    useEffect(() => {
        if(tarSVer){
            init();
        }
    }, [prjId, tarSVer]);

    const init = async () => {
        try {
            const baseline = await getBaseline(prjId, tarSVer);
            setTarType(baseline.pblType);
        } catch (err) {
            console.log(err);
        }
    }

    const buttonMerge = <Button type="primary" onClick={() => openModal(<MergeDlg prjId={prjId} srcSVer={srcSVer} tarSVer={tarSVer} onCall={onCall}/>)} icon={<MergeOutlined/>}>合并分支</Button>

    return tarSVer ? (
            (tarType && 'BASELINE' == tarType) ?
                buttonMerge
                : null
        ) :
        buttonMerge
}

// 供模件制品列表使用
export const CreateMerge = ({ prjId, srcSVer, tarSVer, onCall }) => {
    const openModal = useOpenModal();
    return (
            <Menu.Item key='0' onClick={() => openModal(<MergeDlg prjId={prjId} srcSVer={srcSVer} tarSVer={tarSVer} onCall={onCall}/>)} icon={<MergeOutlined />}>合并分支</Menu.Item>
    );
}

export default Pane;
